import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "./api";
import {handleResponseStatus, updateIdField, updateItem} from "./utils";

const initialState = {
    items: [],
    settlement: [],
    createStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    fetchStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    fetchByIdStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    fetchVendorStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    updateStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    deleteStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    fetchSettlementStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    pagination: {},
    error: null
};

export const fetchPurchase = createAsyncThunk('purchase/fetchPurchase', async ({
                                                                                   scopeId,
                                                                                   roleSign,
                                                                                   date,
                                                                                   page,
                                                                                   limit,
                                                                               } = {}) => {
    try {
        let response;
        const params = new URLSearchParams();
        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (date) params.append("date", date);

        const queryString = params.toString();
        const fullParams = queryString ? `&${queryString}` : '';

        response = await api.get(`/${roleSign}/purchase-orders?id=${scopeId}${fullParams}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const createPurchase = createAsyncThunk('purchase/createPurchase', async ({roleSign, data}) => {
    try {
        const response = await api.post(`${roleSign}/purchase-orders`, data, {
            useMultipart: true,
        });
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const deletePurchase = createAsyncThunk('purchase/deletePurchase', async ({roleSign, id}) => {
    try {
        const response = await api.delete(`/${roleSign}/purchase-orders/${id}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const updatePurchase = createAsyncThunk('purchase/updatePurchase', async ({scopeId, roleSign, data, id}) => {
    try {
        let response;
        response = await api.patch(`/${roleSign}/purchase-orders/${id}?id=${scopeId}`, data, {
            useMultipart: true,
        });
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const fetchPurchaseById = createAsyncThunk('purchase/fetchPurchaseById', async ({roleSign, scopeId, id}) => {
    try {
        let response;
        response = await api.get(`/${roleSign}/purchase-orders/${id}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const fetchVendorPurchase = createAsyncThunk('purchase/fetchVendorPurchase', async ({
                                                                                               roleSign,
                                                                                               scopeId,
                                                                                               id,
                                                                                               date,
                                                                                               page,
                                                                                               limit,
                                                                                           } = {}) => {
    try {
        let response;
        const params = new URLSearchParams();
        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (date) params.append("date", date);

        const queryString = params.toString();
        const fullParams = queryString ? `&${queryString}` : '';
        response = await api.get(`/${roleSign}/purchase-orders/?id=${scopeId}&vendor=${id}${fullParams}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const fetchSettlementPurchase = createAsyncThunk('purchase/fetchSettlementPurchase', async ({
                                                                                                       roleSign,
                                                                                                       scopeId,
                                                                                                       id
                                                                                                   }) => {
    try {
        let response;
        response = await api.get(`/${roleSign}/purchase-orders/settlement?purchase=${id}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

const purchaseSlice = createSlice({
    name: "purchase",
    initialState,
    reducers: {
        resetCreateStatus(state) {
            state.createStatus = 'idle';
        },
        resetUpdateStatus(state) {
            state.updateStatus = 'idle';
        },
        resetDeleteStatus(state) {
            state.deleteStatus = 'idle';
        },
        resetEditStatus(state) {
            state.editStatus = 'idle';
        }
    },
    extraReducers: (builder) => {
        builder

            //create Purchase
            .addCase(createPurchase.pending, (state) => {
                state.createStatus = 'loading';
            })
            .addCase(createPurchase.fulfilled, (state, action) => {
                const {createStatus, error} = handleResponseStatus(action.payload, 'createStatus');
                state.createStatus = createStatus;
                state.error = error;
            })
            .addCase(createPurchase.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.error = action.error.message;
            })


            // Fetch Purchase
            .addCase(fetchPurchase.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchPurchase.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, 'fetchStatus');
                const {data, ...pagination} = action.payload.data;
                state.items = fetchStatus === "success" ? updateIdField(data) : state.items;
                state.fetchStatus = fetchStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchPurchase.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch Purchase by ID
            .addCase(fetchPurchaseById.pending, (state) => {
                state.fetchByIdStatus = 'loading';
            })
            .addCase(fetchPurchaseById.fulfilled, (state, action) => {
                const {fetchByIdStatus, error} = handleResponseStatus(action.payload, 'fetchByIdStatus');
                state.items = fetchByIdStatus === "success" ? updateItem(action.payload.data.data, state.items) : state.items;
                state.fetchByIdStatus = fetchByIdStatus;
                state.error = error;
            })
            .addCase(fetchPurchaseById.rejected, (state, action) => {
                state.fetchByIdStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch Vendor purchase
            .addCase(fetchVendorPurchase.pending, (state) => {
                state.fetchVendorStatus = 'loading';
            })
            .addCase(fetchVendorPurchase.fulfilled, (state, action) => {
                const {fetchVendorStatus, error} = handleResponseStatus(action.payload, 'fetchVendorStatus');
                const {data, ...pagination} = action.payload.data;
                state.items = fetchVendorStatus === "success" ? updateIdField(data) : state.items;
                state.pagination = pagination;
                state.fetchVendorStatus = fetchVendorStatus;
                state.error = error;
            })
            .addCase(fetchVendorPurchase.rejected, (state, action) => {
                state.fetchVendorStatus = 'failed';
                state.error = action.error.message;
            })

            // Update Purchase
            .addCase(updatePurchase.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updatePurchase.fulfilled, (state, action) => {
                const {updateStatus, error} = handleResponseStatus(action.payload, 'updateStatus');
                state.updateStatus = updateStatus;
                state.error = error;
            })
            .addCase(updatePurchase.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.error.message;
            })

            // Delete Purchase
            .addCase(deletePurchase.pending, (state) => {
                state.deleteStatus = 'loading';
            })
            .addCase(deletePurchase.fulfilled, (state, action) => {
                const {deleteStatus, error} = handleResponseStatus(action.payload, 'deleteStatus');
                state.deleteStatus = deleteStatus;
                state.items = state.items.filter((item) => item.id !== action.payload.data.data._id);
                state.error = error;
            })
            .addCase(deletePurchase.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch settlement
            .addCase(fetchSettlementPurchase.pending, (state) => {
                state.fetchSettlementStatus = 'loading';
            })
            .addCase(fetchSettlementPurchase.fulfilled, (state, action) => {
                const {fetchSettlementStatus, error} = handleResponseStatus(action.payload, 'fetchSettlementStatus');
                const {data} = action.payload.data;
                state.settlement = fetchSettlementStatus === "success" ? updateIdField(data) : state.settlement;
                state.fetchSettlementStatus = fetchSettlementStatus;
                state.error = error;
            })
            .addCase(fetchSettlementPurchase.rejected, (state, action) => {
                state.fetchSettlementStatus = 'failed';
                state.error = action.error.message;
            });
    }
});

export const {resetCreateStatus, resetUpdateStatus, resetDeleteStatus, resetEditStatus} = purchaseSlice.actions;
export default purchaseSlice.reducer;
